import { Component, OnDestroy, OnInit } from '@angular/core';
import { Platform } from '@ionic/angular';
import { SwUpdate } from '@angular/service-worker';
import { TranslateService } from '@ngx-translate/core';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { NGXLogger } from 'ngx-logger';
import { SelectionStateService } from './services/selection-state.service';
import { register } from 'swiper/element/bundle';
import { CustomToastService } from './services/custom-toast.service';
import { FirebaseMessagingService } from './services/firebase-messaging.service';
import { PushService } from './services/push.service';
import { AppVersionService } from './services/app-version.service.ts.service';
import { ModalController } from '@ionic/angular';
import { VersionUpdateModalComponent } from './shared/components/modals/version-update-modal/version-update-modal.component';
import { NotificationBadgeService } from './services/notification-badge.service';
import { TurnstileWebSocketService } from './services/turnstile/turnstile-websocket.service';

register();
@Component({
  selector: 'app-root',
  templateUrl: 'app.component.html',
  styleUrls: ['app.component.scss'],
  standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  toastMessage = '';
  toastShow = false;
  toastType: 'success' | 'info' | 'error' = 'success';

  constructor(
    private readonly _platform: Platform,
    private readonly _swUpdate: SwUpdate,
    private readonly _translate: TranslateService,
    private readonly _logger: NGXLogger,
    private readonly _selectionState: SelectionStateService,
    private toastService: CustomToastService,
    private fcmService: FirebaseMessagingService,
    private pushService: PushService,
    private appVersionService: AppVersionService,
    private modalController: ModalController,
    private notificationBadgeService: NotificationBadgeService,
    private turnstileWebSocketService: TurnstileWebSocketService,
  ) {
    this.initTranslation();
    this.toastService.toast$.subscribe(({ message, show, type }) => {
      this.toastMessage = message;
      this.toastShow = show;
      this.toastType = type;
    });
  }

  onHideToast() {
    this.toastShow = false;
  }

  /**
   * Init translation
   */
  private initTranslation() {
    this._translate.setDefaultLang('es');
  }

  ngOnInit() {
    this.initializeApp();
  }

  /**
   * Initialize App
   */
  initializeApp() {
    this._platform.ready().then(() => {
      this._logger.info('Platform ready from');

      // PWA updater
      !Capacitor.isNativePlatform &&
        this.pwaUpdate();

      // Init
      this.init();

      if (Capacitor.isNativePlatform()) {
        this.checkAppVersion();

        import('@capacitor/splash-screen').then(({ SplashScreen }) => {
          SplashScreen.hide();
        });

        // Inicializar FCM (permisos, token y listeners)
        this.fcmService.initialize();
        // Registrar dispositivo, listeners y heartbeat
        this.pushService.init();

        this.setupAppResumeListener();
      }

      this.setupAppCloseListener();
    });
  }

  /**
   * init
   */
  private async init() {
    const isLoggedIn = await this._selectionState.isUserLoggedIn();

    if (isLoggedIn) {
      await this._selectionState.initialize();
    } else {
      this._selectionState.markAsInitialized();
    }

    // Sentry context removed due to deprecated external DSN (TD-226)
    // TODO: Restore Sentry context if a new internal DSN is configured
    // this._utils.setSentryContext();
  }

  private async checkAppVersion() {
    try {
      this._logger.info('Checking app version...');

      this.appVersionService.checkVersion().subscribe({
        next: (response) => {
          if (response.success && response.needs_update && response.latest_version) {
            this._logger.info('App update needed:', response.latest_version);
            this.showUpdateModal(response.latest_version);
          } else {
            this._logger.info('App is up to date');
          }
        },
        error: (error) => {
          this._logger.error('Error checking app version:', error);
        }
      });
    } catch (error) {
      this._logger.error('Error in checkAppVersion:', error);
    }
  }

  private async showUpdateModal(latestVersion: any) {
    try {
      const modal = await this.modalController.create({
        component: VersionUpdateModalComponent,
        componentProps: {
          latestVersionInfo: latestVersion,
          currentVersion: await this.getCurrentAppVersion()
        },
        backdropDismiss: !latestVersion.is_required,
        canDismiss: !latestVersion.is_required
      });

      await modal.present();
    } catch (error) {
      this._logger.error('Error showing update modal:', error);
    }
  }


  private async getCurrentAppVersion(): Promise<string> {
    try {
      if (Capacitor.isNativePlatform()) {
        const { App } = await import('@capacitor/app');
        const appInfo = await App.getInfo();
        return appInfo.version;
      } else {
        return '1.3.50';
      }
    } catch (error) {
      this._logger.error('Error getting current app version:', error);
      return '1.0.0';
    }
  }

  private pwaUpdate() {
    if (this._swUpdate.isEnabled) {
      this._swUpdate.activateUpdate().then((res) => {
        this._logger.info('New version available: ', res);
        this._translate.get("core.new-update-message").subscribe((res: string) => {
          if (
            confirm(res)
          ) {
            window.location.reload()
          }
        });
      });
    }
  }

  private setupAppResumeListener(): void {
    // Listener para cuando la app vuelve del segundo plano (solo nativo)
    App.addListener('appStateChange', ({ isActive }) => {
      if (isActive) {
        this._logger.info('App volvió del segundo plano, actualizando badge de notificaciones...');
        this.notificationBadgeService.loadUnreadCountFromServer();
      }
    });
  }

  private setupAppCloseListener(): void {
    if (!Capacitor.isNativePlatform()) {
      window.addEventListener('beforeunload', () => {
        this._logger.info('[AppComponent] Cerrando app (navegador), desconectando WebSocket...');
        this.turnstileWebSocketService.disconnect();
      });
    }
  }

  ngOnDestroy(): void {
    this._logger.info('[AppComponent] Destruyendo componente, desconectando WebSocket...');
    this.turnstileWebSocketService.disconnect();
  }

}
